import { useGetAllMarketsQuery, useLockMarketMutation, useUpdateBettingStatusMutation } from '../services/user/userApi';
import { toast } from 'react-toastify';

const Toggle = ({ checked, onChange }) => (
  <button
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-300'}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

const BlockActionModal = ({ isOpen, onClose, user }) => {
  const userId = user?.id || user?._id;

  const { data: marketsData, isLoading, isFetching, error, refetch } = useGetAllMarketsQuery(userId, {
    skip: !isOpen || !userId,
    refetchOnMountOrArgChange: true,
  });
  const [lockMarket, { isLoading: isLocking }] = useLockMarketMutation();
  const [updateBettingStatus, { isLoading: isUpdating }] = useUpdateBettingStatusMutation();

  const markets = marketsData?.data || [];

  // Derive "all" from all markets being locked
  const allLocked = markets.length > 0 && markets.every(m => m.isLocked);

  const handleToggle = async (type, currentLocked) => {
    try {
      await lockMarket({ type, locked: !currentLocked, userId }).unwrap();
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update');
    }
  };

  const handleAllToggle = async () => {
    try {
      await updateBettingStatus({
        userId,
        blocked: !allLocked,
        type: 'all',
        cascade: true,
      }).unwrap();
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update');
    }
  };

  if (!isOpen) return null;

  const busy = isLoading || isFetching || isUpdating || isLocking;

  return (
    <>
      <div className="fixed inset-0 bg-opacity-50 z-40" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="bg-[#2d4a54] px-6 py-4 flex items-center justify-between rounded-t-lg">
            <h2 className="text-white text-lg font-medium">BLOCK ACTION FOR {user?.userId || ''}</h2>
            <button onClick={onClose} className="text-white hover:text-gray-300 text-2xl font-light leading-none">×</button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {busy ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : error ? (
              <div className="text-red-600 text-center py-4">Error loading markets</div>
            ) : (
              <>
                {/* All */}
                <div className="flex items-center justify-between py-2 border-b border-gray-200 pb-3">
                  <label className="text-gray-700 text-base font-semibold">All</label>
                  <Toggle checked={allLocked} onChange={handleAllToggle} />
                </div>

                {/* Dynamic markets from API */}
                {markets.map(market => (
                  <div key={market.id} className="flex items-center justify-between py-2">
                    <label className="text-gray-700 text-base">{market.name}</label>
                    <Toggle
                      checked={market.isLocked}
                      onChange={() => handleToggle(market.id, market.isLocked)}
                    />
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 flex justify-end gap-3 border-t">
            <button onClick={onClose} className="px-6 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button onClick={onClose} className="px-8 py-2 bg-[#2d4a54] text-white rounded hover:bg-[#1f3238] transition-colors">
              OK
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlockActionModal;
